import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import SortableHeader from '@/components/SortableHeader'
import { HoverHelp, TableToolbar } from '@/tableControls'
import { useMaxMd } from '@/hooks/useMaxMd'
import { useSortableTable } from '@/hooks/useSortableTable'
import { getDailyThreshold, getProjectedThreshold } from '@/services/waterfall'
import {
  readPersistedSelectedCaseId,
  useProjectionsStore,
} from '@/store/projectionsStore'
import type { DailyThresholdRow, ProjectedThresholdResponse } from '@/types/waterfall'
import { formatApiError } from '@/utils/apiError'
import { todayIsoDate } from '@/utils/waterfallCalculator'
import {
  balanceToY,
  buildOverlayPoints,
  chartXBounds,
  computeXLabelDates,
  dateToX,
  downsampleThresholdRows,
  monthEndChartRows,
  splitActualForChart,
  yMaxForBalances,
  type ChartHoverPoint,
} from './hpThresholdChart'
import WaterfallTabHeader from './WaterfallTabHeader'

type LedgerView = 'date' | 'month'

const PANEL_BODY_HEIGHT = '32rem'
const PANEL_BODY_HEIGHT_MOBILE = '20rem'

const STICKY_DATE_WIDTH_CLASS = 'w-[7.625rem] min-w-[7.625rem] max-w-[7.625rem]'
const STICKY_HEAD_CLASS = 'sticky top-0 z-20 bg-gray-50'
const DATE_HEAD_CLASS =
  `sticky left-0 top-0 z-30 ${STICKY_DATE_WIDTH_CLASS} bg-gray-50 border-r border-gray-200 whitespace-nowrap`
const DATE_CELL_CLASS =
  `sticky left-0 z-20 ${STICKY_DATE_WIDTH_CLASS} border-r border-gray-200 whitespace-nowrap`

function dateCellBgClass(row: { capitalized: boolean; issuance: number; repayment: number }): string {
  if (row.capitalized) return 'bg-indigo-50'
  if (row.issuance > 0 || row.repayment > 0) return 'bg-amber-50'
  return 'bg-white'
}

const fmtFull = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
})

const rateFmt = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 3,
})

const pctFmt = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const DEFAULT_START = '2022-04-04'

const yAxisFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

function escapeCsvCell(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function exportLedgerCsv(
  rows: DailyThresholdRow[],
  ledgerView: LedgerView,
  startDate: string,
  endDate: string,
) {
  const dateHeader = ledgerView === 'month' ? 'Month' : 'Date'
  const interestHeader = ledgerView === 'month' ? 'Interest' : 'Daily Interest'
  const headers = [
    dateHeader,
    'Instrument',
    'Prime Rate',
    'Opening Balance',
    'Draw',
    'Repayment',
    interestHeader,
    'Capitalized',
    'Closing Balance',
  ]

  const csvRows = rows.map((row) =>
    [
      row.date,
      row.instrument ?? 'Preferred Equity',
      String(row.prime_rate),
      String(row.opening_balance),
      row.issuance > 0 ? String(row.issuance) : '',
      row.repayment > 0 ? String(row.repayment) : '',
      String(row.daily_interest),
      row.capitalized ? 'Yes' : '',
      String(row.closing_balance),
    ]
      .map(escapeCsvCell)
      .join(','),
  )

  const blob = new Blob([[headers.join(','), ...csvRows].join('\n')], {
    type: 'text/csv;charset=utf-8',
  })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `hp-threshold-ledger-${ledgerView}-${startDate}-to-${endDate}.csv`
  anchor.click()
  URL.revokeObjectURL(url)
}

function aggregateByMonth(rows: DailyThresholdRow[]): DailyThresholdRow[] {
  const byMonth = new Map<string, DailyThresholdRow[]>()
  for (const row of rows) {
    const month = row.date.slice(0, 7)
    const existing = byMonth.get(month)
    if (existing) existing.push(row)
    else byMonth.set(month, [row])
  }

  return Array.from(byMonth.entries()).map(([month, monthRows]) => {
    const first = monthRows[0]!
    const last = monthRows[monthRows.length - 1]!
    return {
      date: month,
      prime_rate: last.prime_rate,
      opening_balance: first.opening_balance,
      issuance: monthRows.reduce((sum, row) => sum + row.issuance, 0),
      repayment: monthRows.reduce((sum, row) => sum + row.repayment, 0),
      daily_interest: monthRows.reduce((sum, row) => sum + row.daily_interest, 0),
      accrued_uncapitalized: last.accrued_uncapitalized,
      capitalized: monthRows.some((row) => row.capitalized),
      closing_balance: last.closing_balance,
      instrument: last.instrument,
    }
  })
}

type BalanceChartProps = {
  startDate: string
  endDate: string
  actualRows: DailyThresholdRow[]
  projectedRows: DailyThresholdRow[]
  maxMd: boolean
}

function BalanceChart({ startDate, endDate, actualRows, projectedRows, maxMd }: BalanceChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const [size, setSize] = useState({ width: 640, height: 360 })
  const [hovered, setHovered] = useState<ChartHoverPoint | null>(null)
  const { width, height } = size
  const pad = { top: 16, right: 16, bottom: 44, left: 80 }
  const innerW = width - pad.left - pad.right
  const innerH = height - pad.top - pad.bottom

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const updateSize = () => {
      setSize({
        width: Math.max(el.clientWidth, 320),
        height: Math.max(el.clientHeight, 200),
      })
    }

    updateSize()
    const observer = new ResizeObserver(updateSize)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const { minMs, maxMs } = chartXBounds({
    startDate,
    endDate,
    actualRows,
    projectedRows,
  })

  const yMax = yMaxForBalances([
    ...actualRows.map((row) => row.closing_balance),
    ...projectedRows.map((row) => row.closing_balance),
  ])

  const mapPoints = (rows: DailyThresholdRow[], projected: boolean) =>
    rows.map((row) => {
      const x = dateToX(row.date, minMs, maxMs, pad.left, innerW)
      const y = balanceToY(row.closing_balance, innerH, pad.top, yMax)
      return { x, y, row, projected }
    })

  const actualPoints = mapPoints(actualRows, false)
  const projectedPoints = mapPoints(projectedRows, true)
  const allPoints = [...actualPoints, ...projectedPoints]

  const actualPolyline = actualPoints.map((p) => `${p.x},${p.y}`).join(' ')
  const projectedPolyline = projectedPoints.map((p) => `${p.x},${p.y}`).join(' ')
  const yTickCount = Math.floor(yMax / 500_000) + 1
  const yTicks = Array.from({ length: yTickCount }, (_, i) => i * 500_000)
  const xLabelDates = computeXLabelDates(minMs, maxMs, maxMd ? 3 : 6)

  const handleMouseMove = (event: MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current
    if (!svg || allPoints.length === 0) return

    const rect = svg.getBoundingClientRect()
    const mouseX = ((event.clientX - rect.left) / rect.width) * width
    let nearest: ChartHoverPoint | null = null
    let minDist = Infinity
    for (const point of allPoints) {
      const dist = Math.abs(point.x - mouseX)
      if (dist < minDist) {
        minDist = dist
        nearest = point
      }
    }
    if (!nearest) return
    setHovered(nearest)
  }

  return (
    <div ref={containerRef} className="relative h-full w-full">
      <svg
        ref={svgRef}
        width={width}
        height={height}
        className="block h-full w-full"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHovered(null)}
      >
        {yTicks.map((value) => {
          const y = balanceToY(value, innerH, pad.top, yMax)
          return (
            <g key={value}>
              <line
                x1={pad.left}
                y1={y}
                x2={width - pad.right}
                y2={y}
                stroke="#e5e7eb"
                strokeWidth={1}
              />
              <text x={pad.left - 8} y={y + 4} textAnchor="end" className="fill-gray-500 text-[10px]">
                {yAxisFmt.format(value)}
              </text>
            </g>
          )
        })}
        {actualPolyline && (
          <polyline fill="none" stroke="#6366f1" strokeWidth={2} points={actualPolyline} />
        )}
        {projectedPolyline && (
          <polyline
            fill="none"
            stroke="#6366f1"
            strokeWidth={2}
            strokeDasharray="8 6"
            strokeLinejoin="round"
            points={projectedPolyline}
          />
        )}
        {hovered && (
          <circle
            cx={hovered.x}
            cy={hovered.y}
            r={4}
            fill="#6366f1"
            className="pointer-events-none"
          />
        )}
        {xLabelDates.map((dateIso, labelIndex) => {
          const x = dateToX(dateIso, minMs, maxMs, pad.left, innerW)
          const isFirst = labelIndex === 0
          const isLast = labelIndex === xLabelDates.length - 1
          const textAnchor = isFirst ? 'start' : isLast ? 'end' : 'middle'
          return (
            <text
              key={`label-${dateIso}`}
              x={x}
              y={height - 12}
              textAnchor={textAnchor}
              className="fill-gray-500 text-[10px]"
            >
              {maxMd ? dateIso.slice(2, 7) : dateIso.slice(0, 7)}
            </text>
          )
        })}
        <rect
          x={pad.left}
          y={pad.top}
          width={innerW}
          height={innerH}
          fill="transparent"
        />
      </svg>
      {hovered && (
        <div
          className="pointer-events-none absolute z-10 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs shadow-md"
          style={{
            left: `${(hovered.x / width) * 100}%`,
            top: `${(hovered.y / height) * 100}%`,
            transform: 'translate(-50%, calc(-100% - 10px))',
          }}
        >
          {hovered.projected && <p className="text-gray-500">Projected</p>}
          <p className="font-medium text-gray-900">{hovered.row.date}</p>
          <p className="tabular-nums text-gray-600">
            {fmtFull.format(hovered.row.closing_balance)}
          </p>
        </div>
      )}
    </div>
  )
}

export default function HpThreshold() {
  const maxMd = useMaxMd()
  const cellPadClass = maxMd ? 'px-2 py-1' : 'px-3 py-1.5'
  const panelBodyHeight = maxMd ? PANEL_BODY_HEIGHT_MOBILE : PANEL_BODY_HEIGHT
  const [startDate, setStartDate] = useState(DEFAULT_START)
  const [endDate, setEndDate] = useState(todayIsoDate())
  const [rows, setRows] = useState<DailyThresholdRow[]>([])
  const [summary, setSummary] = useState<{
    balance: number
    accrued_uncapitalized: number
    economic_balance: number
  } | null>(null)
  const [ledgerView, setLedgerView] = useState<LedgerView>('date')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [projectedCaseName, setProjectedCaseName] = useState<string | null>(null)
  const [projectedSpliceDate, setProjectedSpliceDate] = useState<string | null>(null)
  const [projectedRows, setProjectedRows] = useState<DailyThresholdRow[]>([])
  const [caseId] = useState(
    () => useProjectionsStore.getState().selectedCaseId ?? readPersistedSelectedCaseId(),
  )

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getDailyThreshold({ start: startDate, end: endDate })
      setRows(data.rows)
      setSummary(data.summary)
    } catch (e) {
      setError(formatApiError(e, 'Failed to load daily ledger'))
      setRows([])
      setSummary(null)
    } finally {
      setLoading(false)
    }
  }, [endDate, startDate])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    let cancelled = false
    const params = caseId != null ? { case_id: caseId } : {}
    void getProjectedThreshold(params)
      .then((data: ProjectedThresholdResponse) => {
        if (cancelled) return
        if (data.rows.length === 0 || data.reason) {
          setProjectedRows([])
          setProjectedCaseName(null)
          setProjectedSpliceDate(null)
          return
        }
        setProjectedRows(data.rows)
        setProjectedCaseName(data.case_name)
        setProjectedSpliceDate(data.splice_date)
      })
      .catch(() => {
        if (cancelled) return
        setProjectedRows([])
        setProjectedCaseName(null)
        setProjectedSpliceDate(null)
      })
    return () => {
      cancelled = true
    }
  }, [caseId])

  const chartActualRows = useMemo(() => {
    const splice = projectedSpliceDate
    const solid = splitActualForChart(rows, splice, todayIsoDate())
    const pin = splice ? [splice] : []
    return downsampleThresholdRows(solid, 400, pin)
  }, [projectedSpliceDate, rows])

  const chartProjectedRows = useMemo(() => {
    if (projectedRows.length === 0) {
      return []
    }
    const overlay = buildOverlayPoints(rows, projectedRows, projectedSpliceDate)
    return monthEndChartRows(overlay)
  }, [projectedRows, projectedSpliceDate, rows])

  const showProjectedLegend = chartProjectedRows.length > 0 && projectedCaseName

  const ledgerRows = useMemo(
    () => (ledgerView === 'month' ? aggregateByMonth(rows) : rows),
    [ledgerView, rows],
  )

  const { sortedData: sortedLedgerRows, requestSort, getSortDirection } =
    useSortableTable<DailyThresholdRow>(ledgerRows, { key: 'date', direction: 'desc' })

  const totalRepaid = useMemo(
    () => rows.reduce((sum, row) => sum + row.repayment, 0),
    [rows],
  )

  const repaidPctOfEconomic =
    summary && summary.economic_balance > 0
      ? totalRepaid / summary.economic_balance
      : null

  return (
    <div className="w-full space-y-6">
      <WaterfallTabHeader
        title="Balances and ledger"
        description="Daily preferred equity ledger from the Demand Note Payable opening through today."
      />

      <div data-readonly-allow className="flex flex-wrap items-end gap-4">
        <label className="block text-sm">
          <span className="font-medium text-gray-700">Start date</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="mt-1 block rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-gray-700">End date</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="mt-1 block rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
      </div>

      {error && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      {summary && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[
            ['Capitalized balance', summary.balance],
            ['Accrued pref', summary.accrued_uncapitalized],
            ['Economic balance', summary.economic_balance],
            ['Total repaid', totalRepaid],
          ].map(([label, value]) => (
            <div
              key={String(label)}
              className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
            >
              <p className="text-xs text-gray-500">{label}</p>
              <p className="mt-1 text-lg font-semibold tabular-nums">
                {fmtFull.format(Number(value))}
                {label === 'Total repaid' && repaidPctOfEconomic != null && (
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    {pctFmt.format(repaidPctOfEconomic).replace('%', '% repaid')}
                  </span>
                )}
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-2 xl:items-stretch">
        <div className="flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-4 py-3">
            <p className="text-sm font-medium text-gray-900">Balance trend</p>
            {showProjectedLegend && (
              <p className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                <span
                  className="inline-block h-0 w-5 shrink-0 border-t-2 border-dashed border-indigo-500"
                  aria-hidden
                />
                Projected · {projectedCaseName}
              </p>
            )}
          </div>
          <div style={{ height: panelBodyHeight }}>
            {loading ? (
              <p className="p-4 text-sm text-gray-500">Loading chart…</p>
            ) : chartActualRows.length > 0 ? (
              <BalanceChart
                startDate={startDate}
                endDate={endDate}
                actualRows={chartActualRows}
                projectedRows={chartProjectedRows}
                maxMd={maxMd}
              />
            ) : (
              <p className="p-4 text-sm text-gray-500">No data for this range.</p>
            )}
          </div>
        </div>

        <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
            <p className="text-sm font-medium text-gray-900">Ledger</p>
            <div data-readonly-allow className="flex items-center gap-2">
              <div className="flex overflow-hidden rounded-md border border-gray-200 text-xs">
                {(['date', 'month'] as const).map((view) => (
                  <button
                    key={view}
                    type="button"
                    onClick={() => setLedgerView(view)}
                    className={`px-3 py-1.5 ${
                      ledgerView === view
                        ? 'bg-brand-50 font-medium text-brand-800'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {view === 'date' ? 'By date' : 'By month'}
                  </button>
                ))}
              </div>
              <TableToolbar
                onExport={() =>
                  exportLedgerCsv(sortedLedgerRows, ledgerView, startDate, endDate)
                }
                exportDisabled={loading || sortedLedgerRows.length === 0}
              />
            </div>
          </div>
          {loading ? (
            <p className="p-4 text-sm text-gray-500">Loading daily ledger…</p>
          ) : (
            <div
              className="table-scroll-mobile overflow-x-auto overflow-y-auto overscroll-x-contain"
              style={{ height: panelBodyHeight }}
            >
              <table className="table-forecast min-w-full border-separate border-spacing-0 text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader
                      label={ledgerView === 'month' ? 'Month' : 'Date'}
                      sortKey="date"
                      sortDirection={getSortDirection('date')}
                      onSort={requestSort}
                      className={`${DATE_HEAD_CLASS} ${cellPadClass} cursor-pointer select-none text-left hover:bg-gray-100`}
                    />
                    <th className={`${STICKY_HEAD_CLASS} ${cellPadClass} text-left`}>
                      {maxMd ? (
                        <span className="sr-only">
                          <HoverHelp label="Instrument" aria-label="Instrument help">
                            Preferred equity instrument for this HP threshold ledger row (defaults
                            to Preferred Equity).
                          </HoverHelp>
                        </span>
                      ) : (
                        <HoverHelp label="Instrument" aria-label="Instrument help">
                          Preferred equity instrument for this HP threshold ledger row (defaults to
                          Preferred Equity).
                        </HoverHelp>
                      )}
                    </th>
                    <th className={`${STICKY_HEAD_CLASS} ${cellPadClass} text-right`}>
                      <HoverHelp label="Prime" aria-label="Prime rate help">
                        WSJ Prime rate used to accrue daily interest on a 360-day year
                        (Actual/360).
                      </HoverHelp>
                    </th>
                    <th className={`${STICKY_HEAD_CLASS} ${cellPadClass} text-right`}>
                      <HoverHelp
                        label={maxMd ? 'Open' : 'Opening'}
                        aria-label="Opening balance help"
                      >
                        Capitalized preferred equity balance at the start of the day, before draws,
                        repayments, and interest.
                      </HoverHelp>
                    </th>
                    <th className={`${STICKY_HEAD_CLASS} ${cellPadClass} text-right`}>
                      <HoverHelp label="Draw" aria-label="Draw help">
                        New preferred equity issued (drawn) on this day.
                      </HoverHelp>
                    </th>
                    <th className={`${STICKY_HEAD_CLASS} ${cellPadClass} text-right`}>
                      <HoverHelp label="Repay" aria-label="Repayment help">
                        Principal repayment of preferred equity on this day.
                      </HoverHelp>
                    </th>
                    <th className={`${STICKY_HEAD_CLASS} ${cellPadClass} text-right`}>
                      <HoverHelp
                        label={
                          ledgerView === 'month' ? 'Interest' : maxMd ? 'Int.' : 'Daily int.'
                        }
                        aria-label="Interest help"
                      >
                        {ledgerView === 'month'
                          ? 'Total interest accrued in the month at Prime on the post-flow daily balance (Actual/360).'
                          : 'Interest accrued this day at Prime on the post-flow daily balance (Actual/360).'}
                      </HoverHelp>
                    </th>
                    <th className={`${STICKY_HEAD_CLASS} ${cellPadClass} text-center`}>
                      <HoverHelp label="Cap" aria-label="Capitalization help">
                        Accrued interest capitalized into principal on the last business day of the
                        calendar quarter.
                      </HoverHelp>
                    </th>
                    <th className={`${STICKY_HEAD_CLASS} ${cellPadClass} text-right`}>
                      <HoverHelp label="Closing" aria-label="Closing balance help">
                        Capitalized preferred equity balance at end of day after draws, repayments,
                        and any quarter-end capitalization.
                      </HoverHelp>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedLedgerRows.map((row) => (
                    <tr
                      key={row.date}
                      className={
                        row.capitalized
                          ? 'bg-indigo-50/60'
                          : row.issuance > 0 || row.repayment > 0
                            ? 'bg-amber-50/40'
                            : undefined
                      }
                      title={row.memo || undefined}
                    >
                      <td className={`${DATE_CELL_CLASS} ${dateCellBgClass(row)} ${cellPadClass}`}>
                        {row.date}
                      </td>
                      <td className={`max-w-[6rem] truncate ${cellPadClass} text-gray-600`}>
                        {row.instrument ?? 'Preferred Equity'}
                      </td>
                      <td className={`${cellPadClass} text-right tabular-nums`}>
                        {rateFmt.format(row.prime_rate)}
                      </td>
                      <td className={`${cellPadClass} text-right tabular-nums`}>
                        {fmtFull.format(row.opening_balance)}
                      </td>
                      <td className={`${cellPadClass} text-right tabular-nums text-green-700`}>
                        {row.issuance > 0 ? fmtFull.format(row.issuance) : '—'}
                      </td>
                      <td className={`${cellPadClass} text-right tabular-nums text-red-700`}>
                        {row.repayment > 0 ? fmtFull.format(row.repayment) : '—'}
                      </td>
                      <td className={`${cellPadClass} text-right tabular-nums`}>
                        {fmtFull.format(row.daily_interest)}
                      </td>
                      <td className={`${cellPadClass} text-center`}>{row.capitalized ? '✓' : ''}</td>
                      <td className={`${cellPadClass} text-right font-medium tabular-nums`}>
                        {fmtFull.format(row.closing_balance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
